<template>
  <div
    :class="[
      `design-system-input flex w-full rounded-lg border transition`,
      `outline-2 -outline-offset-1 focus-within:outline`,
      isDisabled && `opacity-50`,
      fieldContext?.validationStatus.value === 'error'
        ? `border-red-300 outline-red-500 dark:border-red-800`
        : `outline-blue-500`,
    ]"
  >
    <textarea
      :id="fieldContext?.inputId"
      ref="input"
      v-model="modelValue"
      :class="[
        `block w-full resize-none border-none bg-transparent px-2.5 py-1.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-0`,
      ]"
      :placeholder="placeholder"
      :readonly="readonly"
      :required="fieldContext?.required.value"
      :disabled="isDisabled"
      :name="props.name"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      v-bind="$attrs"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, useTemplateRef } from "vue";

import { useTextareaAutosize } from "@/lib/composables/textarea-autosize";

import { injectFieldContext } from "./context";

defineOptions({
  inheritAttrs: false,
});

const modelValue = defineModel<string>({ default: "" });

const props = defineProps<{
  placeholder?: string;
  readonly?: boolean;
  disabled?: boolean;
  name?: string;
  autofocus?: boolean;
}>();

const input = useTemplateRef("input");

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  const ids: string[] = [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ];

  return ids.join(" ");
});

useTextareaAutosize(input, modelValue, { minRows: 3, maxRows: 12 });

onMounted(() => {
  if (props.autofocus) {
    input.value?.focus({ preventScroll: true });
  }
});

defineExpose({ input });
</script>
